import { memo, useState } from "react";
import { Handle, Position } from "@xyflow/react";
import { 
  Image as ImageIcon, 
  Download, 
  Maximize2, 
  MoreHorizontal,
  Trash2,
  Upload,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";

const VisualImageNode = memo(({ data, selected, id }) => {
  const [imageUrl, setImageUrl] = useState(data.config?.image_url || null);
  const isOutput = data.nodeType === "output";

  return (
    <div
      className={`group relative rounded-xl transition-all ${
        selected 
          ? "ring-2 ring-indigo-500 ring-offset-2 ring-offset-[#000000]" 
          : "ring-1 ring-slate-700/50 hover:ring-slate-600"
      }`}
      style={{ minWidth: 200, maxWidth: 300 }}
    >
      {/* Input Handle */}
      {!isOutput && data.nodeType !== "imageInput" && (
        <Handle
          type="target"
          position={Position.Left}
          id="input"
          className="!w-5 !h-5 !bg-blue-500 !border-[3px] !border-slate-900 hover:!bg-blue-400 hover:!scale-125 !transition-all !cursor-crosshair"
        />
      )}

      {/* Header */}
      <div className="bg-slate-900/90 backdrop-blur px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-xs font-medium text-slate-200 truncate max-w-[120px]">
            {data.label || "Image"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
            {isOutput ? "Output" : "Nano"}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 hover:bg-slate-800 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreHorizontal className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-900 border-slate-700">
              <DropdownMenuItem 
                onClick={() => data.onDelete?.()} 
                className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Image Area */}
      <div className="bg-slate-800/50 aspect-[4/3] relative">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={data.label} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500">
            <Upload className="w-8 h-8 mb-2 opacity-50" />
            <span className="text-xs">Drop image here</span>
          </div>
        )}

        {/* Action buttons on hover */}
        {imageUrl && (
          <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button className="p-1.5 bg-slate-900/80 backdrop-blur rounded hover:bg-slate-800">
              <Download className="w-3.5 h-3.5 text-slate-300" />
            </button>
            <button className="p-1.5 bg-slate-900/80 backdrop-blur rounded hover:bg-slate-800">
              <Maximize2 className="w-3.5 h-3.5 text-slate-300" />
            </button>
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-5 !h-5 !bg-blue-500 !border-[3px] !border-slate-900 hover:!bg-blue-400 hover:!scale-125 !transition-all !cursor-crosshair"
      />
    </div>
  );
});

VisualImageNode.displayName = "VisualImageNode";
export default VisualImageNode;
